const FAILED_SUBSCRIPTION_STATUSES = ["past_due", "unpaid", "open", "incomplete", "incomplete_expired"];

export const TENANT_ISSUE_LABELS = {
  suspended: "Suspended",
  read_only: "Read-only",
  active_subscription: "Paying subscription",
  billing_failed: "Payment failed",
  on_trial: "On trial",
  no_subscription: "No subscription",
  cancelled: "Subscription cancelled",
};

export const TENANT_ISSUE_VARIANTS = {
  suspended: "danger",
  read_only: "warning",
  active_subscription: "info",
  billing_failed: "danger",
  on_trial: "neutral",
  no_subscription: "neutral",
  cancelled: "warning",
};

export const analyzeTenantAccount = (tenant) => {
  if (!tenant) {
    return {
      isSuspended: false,
      isReadOnly: false,
      onTrial: false,
      hasSubscription: false,
      subscriptionStatus: null,
      billingFailed: false,
      isCancelled: false,
      issues: [],
    };
  }

  const subscription = tenant.subscription || {};
  const flags = tenant.flags || {};
  const billing = tenant.billing || {};
  const subscriptionStatus = subscription.current_status || null;
  const hasSubscription = Boolean(subscription.subscription_id);
  const isSuspended = tenant.status === "suspended";
  const isReadOnly = Boolean(flags.read_only);
  const onTrial = Boolean(flags.on_trial);
  const billingFailed = FAILED_SUBSCRIPTION_STATUSES.includes(subscriptionStatus);
  const isCancelled = ["cancel", "canceled", "cancelled"].includes(
    String(subscriptionStatus || "").toLowerCase()
  );

  const issues = [];
  if (isSuspended) issues.push("suspended");
  if (isReadOnly) issues.push("read_only");
  if (onTrial) issues.push("on_trial");
  if (billingFailed) issues.push("billing_failed");
  if (isCancelled) issues.push("cancelled");
  if (hasSubscription && subscription.subscription_status && !isCancelled) {
    issues.push("active_subscription");
  } else if (billing.has_payment_settings !== false && !hasSubscription && !isCancelled) {
    issues.push("no_subscription");
  }

  return {
    isSuspended,
    isReadOnly,
    onTrial,
    hasSubscription,
    subscriptionStatus,
    billingFailed,
    isCancelled,
    issues,
  };
};

export const getSelfServiceEmailGuidance = (user) => {
  switch (user?.user_type) {
    case "sp_standard_user":
    case "sp_cos":
      return {
        title: "Worker self-service",
        steps: [
          "Sign in to the worker portal.",
          "Open Settings → Profile.",
          "Update login email there (updates Cognito and profile).",
        ],
      };
    case "sp_admin":
      return {
        title: "Organisation owner self-service",
        steps: [
          "Sign in as the SP Admin.",
          "Open Organisation / account settings.",
          "Update organisation contact email and user profile details.",
        ],
      };
    case "sp_manager":
    case "hr_officer":
      return {
        title: "Manager self-service",
        steps: [
          "Sign in to the manager portal.",
          "Update profile details from account settings where available.",
          "For login email changes, an SP Admin may need to update the user record.",
        ],
      };
    default:
      return {
        title: "Self-service",
        steps: [
          "Ask the user to sign in and update profile details from their account settings.",
          "If they cannot sign in, resolve login access first.",
        ],
      };
  }
};

export const getTenantSupportPlaybooks = (tenant, permissions = {}) => {
  if (!tenant) {
    return [];
  }

  const account = analyzeTenantAccount(tenant);
  const playbooks = [];

  if (permissions.enterTenant || permissions.ghost) {
    playbooks.push({
      id: "update_org_details",
      title: "Change organisation / contact details",
      description:
        "Business name, ABN, and contact details are managed by the tenant admin inside the portal.",
      steps: [
        "Ask the SP Admin to sign in and update organisation settings.",
        permissions.enterTenant
          ? "Or enter tenant context to assist."
          : null,
        permissions.ghost ? "Ghost as the tenant owner to walk them through it." : null,
      ].filter(Boolean),
      primaryAction: permissions.enterTenant
        ? {
            id: "enter_tenant_path",
            label: "Open org settings",
            path: "/settings/sp-setting",
          }
        : permissions.ghost
          ? { id: "ghost", label: "Ghost as user" }
          : null,
    });
  }

  if (permissions.suspend && !account.isSuspended) {
    playbooks.push({
      id: "pause_service",
      title: "Pause service (block logins)",
      description:
        "Suspends the tenant so users cannot sign in. Does not automatically stop Stripe billing.",
      steps: [
        "Suspend the tenant to block access immediately.",
        permissions.billingManage && account.hasSubscription
          ? "Also cancel the Stripe subscription if billing must stop."
          : null,
      ].filter(Boolean),
      primaryAction: { id: "suspend", label: "Suspend tenant" },
    });
  }

  if (permissions.suspend && account.isSuspended) {
    playbooks.push({
      id: "resume_service",
      title: "Resume service",
      description: "Re-enable tenant logins. Billing may need a new Stripe subscription separately.",
      steps: [
        "Activate the tenant.",
        "Confirm subscription status on the Billing tab or in Stripe.",
      ],
      primaryAction: { id: "activate", label: "Activate tenant" },
    });
  }

  if (permissions.billingManage && account.onTrial) {
    playbooks.push({
      id: "extend_trial",
      title: "Extend trial period",
      description: "Push the trial end date without requiring SP admin action.",
      steps: [
        "Confirm the new trial end date with the customer.",
        "Extend trial in Vertex360 and Stripe subscription when linked.",
      ],
      primaryAction: { id: "extend_trial", label: "Extend trial" },
    });
  }

  if (permissions.billingManage && account.hasSubscription && !account.isCancelled) {
    playbooks.push({
      id: "stop_billing_period_end",
      title: "Stop billing at period end",
      description:
        "No further renewals after the current billing period. Access continues until then unless you also suspend.",
      steps: [
        "Cancel the Stripe subscription at period end.",
        "Confirm the end date with the customer.",
      ],
      primaryAction: {
        id: "cancel_subscription_period_end",
        label: "Cancel at period end",
      },
    });

    playbooks.push({
      id: "stop_billing_now",
      title: "Stop Stripe charges immediately",
      description:
        "Cancels the subscription now and enables read-only mode for the tenant.",
      steps: [
        "Cancel the Stripe subscription immediately.",
        "Tenant moves to read-only; users can still sign in unless suspended.",
      ],
      primaryAction: {
        id: "cancel_subscription_immediate",
        label: "Cancel subscription now",
      },
    });
  }

  if (permissions.billingManage) {
    playbooks.push({
      id: "close_account",
      title: "Close / cancel account",
      description:
        "Full offboarding: cancel Stripe (if any), enable read-only, and suspend the tenant.",
      steps: [
        "Cancel active Stripe subscription.",
        "Enable read-only and suspend tenant access.",
        "Document the closure reason in the audit log.",
      ],
      primaryAction: { id: "close_account", label: "Close account" },
    });
  }

  if (permissions.billingView && account.billingFailed) {
    playbooks.push({
      id: "failed_payment",
      title: "Failed payment / billing issue",
      description: "Review outstanding invoices and payment method in Stripe.",
      steps: [
        permissions.billingView ? "Open the Billing tab for invoice history." : null,
        permissions.billingManage
          ? "Process payment in Stripe or send a reminder email with pay link and invoice PDF."
          : null,
        permissions.billingView
          ? "Use Stripe management to review the customer and subscription."
          : null,
        permissions.readOnly && !account.isReadOnly
          ? "Enable read-only until payment is resolved."
          : null,
      ].filter(Boolean),
      primaryAction: permissions.billingView
        ? { id: "open_billing", label: "Open billing tab" }
        : null,
    });
  }

  if (permissions.readOnly) {
    playbooks.push({
      id: "read_only_override",
      title: account.isReadOnly ? "Lift read-only mode" : "Force read-only mode",
      description: account.isReadOnly
        ? "Allow writes again (e.g. after payment resolved)."
        : "Block writes while keeping logins active.",
      steps: [
        account.isReadOnly
          ? "Lift the read-only override on payment settings."
          : "Enable read-only on payment settings.",
      ],
      primaryAction: {
        id: account.isReadOnly ? "read_only_off" : "read_only_on",
        label: account.isReadOnly ? "Lift read-only" : "Enable read-only",
      },
    });
  }

  return playbooks;
};

export const getTenantStripeLinks = (tenant, permissions = {}) => {
  if (!permissions.billingView) {
    return [];
  }

  const subscription = tenant?.subscription || {};

  return [
    {
      id: "customer",
      label: "Stripe customer",
      resourceId: subscription.stripe_customer_id || null,
      href: subscription.stripe_dashboard_customer_url || null,
    },
    {
      id: "subscription",
      label: "Stripe subscription",
      resourceId: subscription.subscription_id || null,
      href: subscription.stripe_dashboard_subscription_url || null,
    },
  ].filter((link) => link.href || link.resourceId);
};
